import { useCallback } from 'react'
import { useAppDispatch, useAppSelector } from '@crm-src/lib/store'
import {
  fetchTasks,
  fetchCalendarTasks,
  fetchTaskStats,
  createTask as createTaskAction,
  updateTask as updateTaskAction,
  toggleTaskComplete,
  deleteTask as deleteTaskAction,
  cancelTaskOccurrence as cancelTaskOccurrenceAction,
  clearCache,
  clearError,
  optimisticToggleComplete,
  selectTasks,
  selectCalendarTasks,
  selectTasksLoading,
  selectTasksError,
  selectTaskStats,
  selectTasksPagination
} from '@crm-src/lib/store/tasksSlice'
import type { TaskFilters, CreateTaskData, UpdateTaskData } from '@crm-src/types/task'

/**
 * Hook for managing tasks with recurring scheduling capabilities
 */
export const useTasks = () => {
  const dispatch = useAppDispatch()
  
  // Selectors
  const tasks = useAppSelector(selectTasks)
  const calendarTasks = useAppSelector(selectCalendarTasks)
  const isLoading = useAppSelector(selectTasksLoading)
  const error = useAppSelector(selectTasksError)
  const stats = useAppSelector(selectTaskStats)
  const pagination = useAppSelector(selectTasksPagination)

  // Fetch tasks with filters
  const getTasks = useCallback(async (filters: TaskFilters = {}) => {
    try {
      const result = await dispatch(fetchTasks(filters)).unwrap()
      return result
    } catch (error) {
      console.error('Failed to fetch tasks:', error)
      throw error
    }
  }, [dispatch])

  // Fetch tasks for calendar view
  const getCalendarTasks = useCallback(async (params: {
    start_date?: string
    end_date?: string
    assigned_to_id?: number
    status?: string
  }) => {
    try {
      const result = await dispatch(fetchCalendarTasks(params)).unwrap()
      return result
    } catch (error) {
      console.error('Failed to fetch calendar tasks:', error)
      throw error
    }
  }, [dispatch])

  // Get task statistics
  const getTaskStats = useCallback(async (params: { 
    assigned_to_id?: number
    user_id?: number
  } = {}) => {
    try {
      const result = await dispatch(fetchTaskStats(params)).unwrap()
      return result
    } catch (error: any) {
      // Handle 404 gracefully - stats endpoint might not be available
      const is404 = error?.response?.status === 404 || error?.message?.includes('404')
      if (is404) {
        // Silently return null for 404 - endpoint may not be implemented yet
        return null
      }
      // Only log non-404 errors
      console.error('Failed to fetch task stats:', error)
      return null // Return null instead of throwing to prevent unhandled promise rejections
    }
  }, [dispatch])

  // Create new task
  const createTask = useCallback(async (taskData: CreateTaskData) => {
    try {
      const result = await dispatch(createTaskAction(taskData)).unwrap()
      return result
    } catch (error) {
      console.error('Failed to create task:', error)
      throw error
    }
  }, [dispatch])

  // Update task
  const updateTask = useCallback(async (id: string, data: UpdateTaskData) => {
    try {
      const result = await dispatch(updateTaskAction({ id, data })).unwrap()
      return result
    } catch (error) {
      console.error('Failed to update task:', error)
      throw error
    }
  }, [dispatch])

  // Toggle task completion (with optimistic update)
  const toggleComplete = useCallback(async (id: string) => {
    try {
      // For recurring instances (IDs with underscore), skip optimistic update
      // because the state might not be accurate and we refresh the list anyway
      const isRecurringInstance = id.includes('_')
      
      if (!isRecurringInstance) {
        // Optimistic update only for regular tasks
        dispatch(optimisticToggleComplete(id))
      }
      
      // Send to server
      await dispatch(toggleTaskComplete(id)).unwrap()
      return true
    } catch (error) {
      // Revert optimistic update on error (only if we did one)
      const isRecurringInstance = id.includes('_')
      if (!isRecurringInstance) {
        dispatch(optimisticToggleComplete(id))
      }
      console.error('Failed to toggle completion:', error)
      throw error
    }
  }, [dispatch])

  // Delete task
  const deleteTask = useCallback(async (id: string) => {
    try {
      await dispatch(deleteTaskAction(id)).unwrap()
      return true
    } catch (error) {
      console.error('Failed to delete task:', error)
      throw error
    }
  }, [dispatch])

  // Cancel a specific occurrence of a recurring task
  const cancelTaskOccurrence = useCallback(async (taskId: string, occurrenceDate: string) => {
    try {
      await dispatch(cancelTaskOccurrenceAction({ taskId, occurrenceDate })).unwrap()
      return true
    } catch (error) {
      console.error('Failed to cancel occurrence:', error)
      throw error
    }
  }, [dispatch])

  // Utility functions
  const clearTasksCache = useCallback(() => {
    dispatch(clearCache())
  }, [dispatch])

  const clearTasksError = useCallback(() => {
    dispatch(clearError())
  }, [dispatch])

  // Filter helpers (client-side filtering)
  const getOverdueTasks = useCallback(() => {
    return tasks.filter(task => task.overdue)
  }, [tasks])

  const getUpcomingTasks = useCallback(() => {
    return tasks.filter(task => task.upcoming)
  }, [tasks])

  return {
    // State
    tasks,
    calendarTasks,
    isLoading,
    error,
    stats,
    pagination,

    // Actions
    getTasks,
    getCalendarTasks,
    getTaskStats,
    createTask,
    updateTask,
    toggleComplete,
    deleteTask,
    cancelTaskOccurrence,

    // Utilities
    clearTasksCache,
    clearTasksError,

    // Client-side filters
    getOverdueTasks,
    getUpcomingTasks,
  }
}

export default useTasks



